import React, { useMemo } from 'react';
import {
  Box,
  CardContent,
  Typography,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Slide,
  Fade,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IsNotNullOrEmpty } from '../../utils/validation';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DaySchedule {
  start: string;
  end: string;
  isClosed: boolean;
}

interface OperatingHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

interface AgentHoursDialogProps {
  open: boolean;
  onClose: () => void;
  hours: OperatingHours;
  dealershipName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_KEYS: (keyof OperatingHours)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_LABELS: Record<keyof OperatingHours, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

function formatTime(time: string): string {
  if (!time) return '';
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minuteStr} ${period}`;
}

function getCurrentDayKey(): keyof OperatingHours {
  const jsDay = new Date().getDay();
  const order: (keyof OperatingHours)[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];
  return order[jsDay];
}

function isOpenNow(schedule: DaySchedule): boolean {
  if (schedule.isClosed || !schedule.start || !schedule.end) return false;
  const now = new Date();
  const [startH, startM] = schedule.start.split(':').map(Number);
  const [endH, endM] = schedule.end.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return nowMinutes >= startH * 60 + startM && nowMinutes < endH * 60 + endM;
}

// ─── Slide-up transition for mobile bottom sheet ──────────────────────────────

const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

const FadeTransition = React.forwardRef(function FadeTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Fade ref={ref} {...props} />;
});

// ─── Inner Content Component ──────────────────────────────────────────────────

interface HoursContentProps {
  hours: OperatingHours;
  dealershipName: string;
  onClose: () => void;
}

const HoursContent: React.FC<HoursContentProps> = ({
  hours,
  dealershipName,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const todayKey = getCurrentDayKey();
  const todaySchedule = hours[todayKey];
  const openNow = isOpenNow(todaySchedule);

  const rows = useMemo(
    () =>
      DAY_KEYS.map((key) => ({
        key,
        label: DAY_LABELS[key],
        schedule: hours[key],
        isToday: key === todayKey,
      })),
    [hours, todayKey],
  );

  return (
    <>
      {/* ── Dialog Header ─────────────────────────────────────── */}
      {isMobile && (
        // Drag handle for bottom sheet
        <Box
          sx={{ display: 'flex', justifyContent: 'center', pt: 1.25, pb: 0.5 }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: alpha(theme.palette.text.primary, 0.15),
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          px: 3,
          py: 2.25,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          // On mobile, no top radius (sheet handles it); on desktop, match dialog radius
          borderRadius: isMobile ? 0 : 'inherit',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          {/* Icon badge */}
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              background: alpha('#f59e0b', 0.15),
              border: `1px solid ${alpha('#f59e0b', 0.3)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 19, color: '#f59e0b' }} />
          </Box>

          <Box>
            <Typography
              variant='caption'
              sx={{
                color: alpha('#fff', 0.45),
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.62rem',
                fontWeight: 600,
                display: 'block',
                lineHeight: 1,
                mb: 0.3,
              }}
            >
              {dealershipName}
            </Typography>
            <Typography
              variant='subtitle1'
              sx={{
                color: '#fff',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                fontSize: '1rem',
              }}
            >
              Operating Hours
            </Typography>
          </Box>
        </Stack>

        <Stack direction='row' alignItems='center' spacing={1}>
          {/* Open / Closed badge */}
          <Chip
            size='small'
            icon={
              <FiberManualRecordIcon
                sx={{
                  fontSize: '8px !important',
                  color: `${openNow ? '#4ade80' : '#f87171'} !important`,
                }}
              />
            }
            label={openNow ? 'Open Now' : 'Closed Now'}
            sx={{
              height: 26,
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.02em',
              borderRadius: 2,
              border: `1px solid ${openNow ? alpha('#4ade80', 0.35) : alpha('#f87171', 0.35)}`,
              background: openNow
                ? alpha('#4ade80', 0.1)
                : alpha('#f87171', 0.1),
              color: openNow ? '#4ade80' : '#f87171',
              '.MuiChip-icon': { ml: '6px' },
            }}
          />

          {/* Close button */}
          <IconButton
            onClick={onClose}
            size='small'
            aria-label='Close dialog'
            sx={{
              color: alpha('#fff', 0.6),
              '&:hover': {
                background: alpha('#fff', 0.1),
                color: '#fff',
              },
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Today Summary Bar ──────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 1.6,
          background: openNow ? alpha('#4ade80', 0.04) : alpha('#f87171', 0.04),
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          Today ({DAY_LABELS[todayKey]})
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontWeight: 700,
            color: todaySchedule.isClosed ? 'text.disabled' : 'text.primary',
          }}
        >
          {todaySchedule.isClosed
            ? 'Closed'
            : `${formatTime(todaySchedule.start)} – ${formatTime(todaySchedule.end)}`}
        </Typography>
      </Box>

      {/* ── Week Schedule ──────────────────────────────────────── */}
      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        <CardContent sx={{ px: 0, py: '0 !important' }}>
          {rows.map((row, index) => (
            <React.Fragment key={row.key}>
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                  transition: 'background 0.15s ease',
                  background: row.isToday
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'transparent',
                  '&:hover': {
                    background: alpha(theme.palette.action.hover, 0.5),
                  },
                  ...(row.isToday && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: 3,
                      borderRadius: '0 3px 3px 0',
                      background: '#f59e0b',
                    },
                  }),
                }}
              >
                {/* Day label */}
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: row.isToday ? 700 : 500,
                      color: row.isToday ? 'text.primary' : 'text.secondary',
                      minWidth: 96,
                      fontSize: '0.875rem',
                    }}
                  >
                    {row.label}
                  </Typography>
                  {row.isToday && (
                    <Chip
                      label='Today'
                      size='small'
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        borderRadius: 1,
                        background: alpha('#f59e0b', 0.12),
                        color: '#d97706',
                        border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                        '.MuiChip-label': { px: '5px' },
                      }}
                    />
                  )}
                </Stack>

                {/* Hours or Closed */}
                {row.schedule.isClosed ? (
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.disabled',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      fontStyle: 'italic',
                    }}
                  >
                    Closed
                  </Typography>
                ) : (
                  <Stack direction='row' alignItems='center' spacing={0.5}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: row.isToday ? 700 : 500,
                        fontSize: '0.875rem',
                        color: row.isToday ? 'text.primary' : 'text.secondary',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {formatTime(row.schedule.start)}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.disabled', fontSize: '0.8rem' }}
                    >
                      –
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: row.isToday ? 700 : 500,
                        fontSize: '0.875rem',
                        color: row.isToday ? 'text.primary' : 'text.secondary',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {formatTime(row.schedule.end)}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {index < rows.length - 1 && (
                <Divider
                  sx={{ mx: 3, borderColor: alpha(theme.palette.divider, 0.5) }}
                />
              )}
            </React.Fragment>
          ))}
        </CardContent>

        {/* ── Footer ──────────────────────────────────────────── */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: alpha(theme.palette.action.hover, 0.3),
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
              letterSpacing: '0.02em',
            }}
          >
            All times shown in local dealership timezone. Hours may vary on
            holidays.
          </Typography>
        </Box>
      </DialogContent>
    </>
  );
};

// ─── Main Dialog Component ────────────────────────────────────────────────────

const AgentHoursDialog: React.FC<AgentHoursDialogProps> = ({
  open,
  onClose,
  hours,
  dealershipName = 'Dealership',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getHours = () => {
    if (IsNotNullOrEmpty(hours)) {
      return hours;
    } else {
      return {
        monday: { start: '09:00', end: '18:00', isClosed: false },
        tuesday: { start: '09:00', end: '18:00', isClosed: false },
        wednesday: { start: '09:00', end: '18:00', isClosed: false },
        thursday: { start: '09:00', end: '18:00', isClosed: false },
        friday: { start: '09:00', end: '18:00', isClosed: false },
        saturday: { start: '09:00', end: '18:00', isClosed: false },
        sunday: { start: '', end: '', isClosed: true },
      };
    }
  };

  return (
    <>
      {/* ── Responsive Dialog ───────────────────────────────────── */}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='xs'
        // Switch transition: slide-up on mobile, fade on desktop
        TransitionComponent={isMobile ? SlideUpTransition : FadeTransition}
        keepMounted={false}
        aria-labelledby='dealership-hours-dialog'
        PaperProps={{
          elevation: isMobile ? 8 : 4,
          sx: {
            overflow: 'hidden',
            // ── Mobile: bottom sheet ──────────────────────────────
            ...(isMobile
              ? {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  m: 0,
                  width: '100%',
                  maxWidth: '100% !important',
                  borderRadius: '16px 16px 0 0',
                  maxHeight: '92dvh',
                }
              : {
                  // ── Desktop: centered modal ───────────────────────
                  borderRadius: 3,
                  maxHeight: '85vh',
                }),
          },
        }}
        // Push the backdrop slightly so bottom-sheet edge is visible on mobile
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'flex-end' : 'center',
          },
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(2px)',
            background: alpha('#000', 0.45),
          },
        }}
      >
        <HoursContent
          hours={getHours()}
          dealershipName={dealershipName}
          onClose={onClose}
        />
      </Dialog>
    </>
  );
};

export default AgentHoursDialog;

// ─── Example Usage ────────────────────────────────────────────────────────────
//
// import AgentHoursDialog from "./AgentHoursDialog";
//
// const hoursData = {
//   monday:    { start: "10:00", end: "19:00", isClosed: false },
//   tuesday:   { start: "10:00", end: "19:00", isClosed: false },
//   wednesday: { start: "10:00", end: "19:00", isClosed: false },
//   thursday:  { start: "10:00", end: "19:00", isClosed: false },
//   friday:    { start: "10:00", end: "19:00", isClosed: false },
//   saturday:  { start: "10:00", end: "19:00", isClosed: false },
//   sunday:    { start: "",      end: "",      isClosed: true  },
// };
//
// Default trigger button:
// <AgentHoursDialog hours={hoursData} dealershipName="Premier Auto Group" />
//
// Custom trigger:
// <AgentHoursDialog
//   hours={hoursData}
//   dealershipName="Premier Auto Group"
//   trigger={(onOpen) => (
//     <IconButton onClick={onOpen}><AccessTimeIcon /></IconButton>
//   )}
// />
